import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogEntry, CredentialProvider, SavedCredential } from '../../../api/admin';
import { adminMe, getIntegrationCatalog, listCredentials } from '../../../api/admin';
import {
  ConnectionRow, FormSummary, WorkflowGraph, WorkflowNode, copyConnectionApi, createConnectionApi, deleteConnectionApi,
  getFormBuilder, getFormParents, getFormWorkflow, listFormConnections, listForms, querySubmissionsApi,
  saveFormWorkflowApi, submissionQueryString, updateConnectionApi,
} from '../../../api/forms';
import type { FieldDefinition } from '../../../shared/types';
import { continuationParents, flattenWidgetTree, isContinuation } from '../../../shared/types';
import { ParentForm, parentFieldDefs } from '../parentFields';
import { questionsForWorkflow } from './draftQuestions';
import { sectionsFromSpine } from '../fieldGroups';
import PlanLimitBlock from '../../../components/PlanLimitBlock';
import type { PlanLimitBody } from '../../../api/limits';
import { Link, useSearchParams } from 'react-router-dom';
import { useFormWorkspace } from '../FormWorkspace';
import type { PickableForm, ScopeInfo } from '../ConnectionEditors';
import WorkflowCanvas, { WorkflowCanvasHandle, assumedHeight } from './WorkflowCanvas';
import PaletteRail, { PaletteEntry } from './PaletteRail';
import NodeEditorModal from './NodeEditorModal';
import TryItModal from './TryItModal';
import ConnectModal from './ConnectModal';
import {
  attachmentFor, connectionIsOrphaned, graphContextFor, insertNode, newNodeId, relinkAround, undeletable,
  undrawnConnections, validateGraph, withoutDeadPorts,
} from './graph';
import { DEFAULT_VIEW, View, zoomStep } from './view';
import { usePageHeight } from './usePageHeight';
import { CommitOptions, useGraphHistory } from './useGraphHistory';
import './workflow.css';
import { Modal, RowMenu } from '../../../components/fs';
import { NARROW, PHONE, useMediaQuery } from '../../../shared/media';

/**
 * The Workflow tab: what happens when somebody submits this form, drawn.
 *
 * It replaces the Connections tab entirely — removed, not hidden, because two
 * screens writing the same connections with different mental models is worse
 * than either of them.
 *
 * Three states, and which one you meet says something true about the form:
 *
 *   nothing here    — no connections at all: the start pill and one dashed
 *                     ghost, because the page should open as a drawing.
 *   an offer        — connections but no workflow, which is what most accounts
 *                     see first: the graph this form would get, dashed, with
 *                     each node saying where it came from. Nothing is written
 *                     until it is accepted.
 *   a workflow      — adopted. Saves are immediate and live, exactly as
 *                     connections have always been; there is no Publish here
 *                     and the status pill says so.
 *
 * A form that has said "not now" can still open and edit its connections from
 * the proposal, because configuration lives on the connection either way. What
 * it cannot do is rearrange the drawing, since the drawing is not yet what runs.
 *
 * R6 (docs/PLAYBOOK-redesign.md §7 R6): the rail (236px) beside the canvas.
 * A click on a step opens it in the step dialog (`NodeEditorModal`) at every
 * width — R6's 360px side inspector went on 2026-10-08, when Ben asked for
 * the dialog instead: an email's body could not be read in a column that
 * narrow. Below 900 the M6 phone and tablet layout stands: the rail as a
 * sheet behind a "+", the Steps list, the read-only drawing behind a toggle.
 */
/**
 * The tab keeps its own working state. When the draft is replaced from
 * outside it — discarded, or another session's loaded — it starts again
 * from what the server now holds.
 */
export default function WorkflowTab() {
  const { documentEpoch } = useFormWorkspace();
  return <WorkflowTabBody key={documentEpoch} />;
}

function WorkflowTabBody() {
  const { form, draft, noteDocumentDraft, registerBeforePublish } = useFormWorkspace();

  const [connections, setConnections] = useState<ConnectionRow[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  /**
   * The questions this form's LIVE submissions carry.
   *
   * Published, not drafted — because a branch is evaluated against submissions
   * that have already arrived, and those only ever hold what was published
   * when they were sent.
   */
  const [publishedFields, setPublishedFields] = useState<FieldDefinition[]>([]);
  const [widgetScopes, setWidgetScopes] = useState<ScopeInfo[]>([]);
  /**
   * The forms this one continues, with their published questions — empty for
   * a form that continues nothing. A child form's steps and branches offer
   * them as `parent.<key>` beside its own; see `parentFields.ts`.
   */
  const [parents, setParents] = useState<ParentForm[]>([]);
  const [otherForms, setOtherForms] = useState<PickableForm[]>([]);
  const [credProviders, setCredProviders] = useState<CredentialProvider[]>([]);
  const [savedCreds, setSavedCreds] = useState<SavedCredential[]>([]);
  /**
   * Whether this person may MANAGE credentials, as opposed to pick one (Q6).
   *
   * A Member with Edit gets the account branch of the credential picker and an
   * inert "different account" branch — which is also the only branch that
   * renders a secret field, so nothing else needs masking.
   */
  const [canManageCredentials, setCanManageCredentials] = useState(true);

  /** null until loaded; then either the stored graph or null for "not adopted". */
  const [graph, setGraph] = useState<WorkflowGraph | null>(null);
  const [adopted, setAdopted] = useState(false);
  const [proposal, setProposal] = useState<WorkflowGraph | null>(null);
  const [provenance, setProvenance] = useState<Record<string, string>>({});

  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * `?node=<id>` — arriving from somewhere that already knows which step it
   * means. The Document tab's "Where it goes" links here, and a link that
   * dropped somebody on the canvas to hunt for the step would not be an answer.
   *
   * Consumed once and then cleared from the address bar: leaving it there
   * would reopen that node's editor on every reload for the rest of the
   * session, including after the person deliberately closed it.
   */
  const [linkParams, setLinkParams] = useSearchParams();
  const [railOpen, setRailOpen] = useState(true);
  /**
   * The workflow under 900 (§4H).
   *
   * `narrow` is the tablet and the phone: the sub-sentence goes and the
   * palette opens as a sheet rather than a column. `phone` is where the
   * default view is the Steps list and the drawing is behind a toggle, the
   * toolbar folds into one row with a ⋯, and "Add a step" is a floating "+".
   */
  const narrow = useMediaQuery(NARROW);
  const phone = useMediaQuery(PHONE);
  const [view, setView] = useState<'steps' | 'canvas'>('steps');
  const [moreOpen, setMoreOpen] = useState(false);
  /** the palette as a sheet, on a phone and a tablet alike */
  const [railSheet, setRailSheet] = useState(false);
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState<PaletteEntry | null>(null);
  /** Where the drawing sits and how big it is (R6). */
  const [canvasView, setCanvasView] = useState<View>(DEFAULT_VIEW);
  const canvasRef = useRef<WorkflowCanvasHandle>(null);
  const fitted = useRef(false);
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, !phone, !!connections && (graph !== null || proposal !== null));
  const [save, setSave] = useState<{ state: 'idle' | 'saving' | 'error'; message?: string }>({ state: 'idle' });
  const [error, setError] = useState('');
  const [problemsOpen, setProblemsOpen] = useState(false);
  /** The 402 from the plan gate, until a save succeeds. */
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  /** The dry run, open. Sends nothing and saves nothing — see TryItModal. */
  const [trying, setTrying] = useState(false);
  /** The step whose "Connect to…" list is open. */
  const [connecting, setConnecting] = useState<string | null>(null);
  /** the reverse of `connecting`: what should lead TO this step (§4H H4) */
  const [connectingBefore, setConnectingBefore] = useState<string | null>(null);
  const [allForms, setAllForms] = useState<FormSummary[]>([]);
  /**
   * How many submissions are still waiting on somebody.
   *
   * The Follow-up tab used to carry this and the tab is gone — the canvas is
   * the summary now. The COUNT is not a summary of the setup though, it is a
   * fact about the world, so it survives here as a link into the submissions
   * it is counting rather than being dropped with the tab.
   */
  const [awaiting, setAwaiting] = useState<number | null>(null);

  /** Undo, because this page saves live and has no Publish to walk away from. */
  const history = useGraphHistory();
  // The stable half. Putting the whole `history` object in a dependency list
  // re-fires the loaders on every render, which is a request storm.
  const historyActions = history.actions;

  /** "Open" a step: select it, and put its settings up in the step dialog. */
  const openNode = useCallback((id: string) => { setSelected(id); setEditing(id); }, []);

  // Open what the link named, once the drawing it names has loaded.
  useEffect(() => {
    const wanted = linkParams.get('node');
    if (!wanted) return;
    const drawn = graph || proposal;
    if (!drawn) return;                       // still loading; try again next render
    if (drawn.nodes.some((n) => n.id === wanted)) { setSelected(wanted); setEditing(wanted); }
    const next = new URLSearchParams(linkParams);
    next.delete('node');
    setLinkParams(next, { replace: true });
  }, [linkParams, setLinkParams, graph, proposal]);

  const loadConnections = useCallback(
    // The steps as the DRAFT has them (085): this tab rides the draft.
    () => listFormConnections(form.id, { draft: true }).then((r) => setConnections(r.connections)),
    [form.id]
  );
  /** After a step was added, changed or removed: read them again, and the form now has unpublished changes. */
  const stepsChanged = useCallback(() => loadConnections().then(() => { noteDocumentDraft(); }), [loadConnections, noteDocumentDraft]);

  /** Set once a missing workflow has been written, so it is attempted once. */
  const materialised = useRef(false);

  const loadWorkflow = useCallback(() => getFormWorkflow(form.id, { draft: true }).then((r) => {
    setProposal(r.proposal);
    setProvenance(r.provenance || {});
    setAdopted(!!r.workflow);
    setGraph(r.workflow ? r.workflow.graph : null);
    if (r.workflow) historyActions.reset(r.workflow.graph);
  }), [form.id, historyActions]);

  /**
   * Which forms this one continues, as a value rather than an object.
   *
   * The workspace re-reads the form row after every Publish and settings save,
   * and every read hands back a NEW `settings.continuation` object. Keyed on the
   * object, the loader below re-ran on each of them — and in the moment between
   * forgetting the saved graph and reading it again, the "a form with no
   * workflow gets one" effect saved the last proposal over the real drawing.
   * Ooma's Drop Ship form lost its branch to every Publish that way
   * (2026-09-28). What the parents ARE is what the parents fetch needs.
   */
  const continuationKey = isContinuation(form.settings.continuation)
    ? JSON.stringify(continuationParents(form.settings.continuation))
    : '';

  useEffect(() => {
    // The proposal goes with the graph: a proposal left over from the last
    // read is exactly what the materialise effect must never act on.
    setGraph(null); setAdopted(false); setProposal(null); materialised.current = false; fitted.current = false;
    loadConnections().catch((e) => setError(e.message));
    loadWorkflow().catch((e) => setError(e.message));
    getIntegrationCatalog().then((r) => setCatalog([...r.builtIn, ...r.integrations])).catch(() => undefined);
    listCredentials().then((r) => { setCredProviders(r.providers || []); setSavedCreds(r.credentials || []); }).catch(() => undefined);
    adminMe()
      .then((r) => setCanManageCredentials(
        r.isPlatformAdmin || r.admin.role === 'owner' || r.admin.role === 'admin'
      ))
      .catch(() => undefined);
    listForms().then((r) => {
      setAllForms(r.forms);
      setOtherForms(r.forms
        .filter((f) => f.id !== form.id)
        .map((f) => ({ id: f.id, name: f.name, status: f.status })));
    }).catch(() => undefined);
    querySubmissionsApi(form.id, submissionQueryString({
      conditions: [{ column: 'meta:status', op: 'is', value: 'awaiting' }], limit: 1,
    })).then((r) => setAwaiting(r.total ?? null)).catch(() => setAwaiting(null));
    getFormBuilder(form.id).then((b) => {
      setPublishedFields(b.fields);
      setWidgetScopes((b.widgets || []).flatMap((w) => (w.tree
        ? flattenWidgetTree(w.tree).map((n) => ({
          scope: n.scope, label: n.itemLabel, parentScope: n.parentScope, countKey: n.countKey,
          ...(n.counts?.length ? { counts: n.counts.map((c) => ({ key: c.key, label: c.label })) } : {}),
        }))
        : [])));
    }).catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, loadConnections, loadWorkflow]);

  // The parents follow the continuation's VALUE (see `continuationKey`), and
  // re-reading them touches nothing else on the page.
  useEffect(() => {
    if (continuationKey) {
      getFormParents(form.id).then((r) => setParents(r.parents || [])).catch(() => setParents([]));
    } else {
      setParents([]);
    }
  }, [form.id, continuationKey]);

  // The drawing frames itself the first time it is on screen, like the
  // design's prototype: a small workflow sits centred at 100%, a big one is
  // zoomed out until every card is visible.
  useEffect(() => {
    if (fitted.current || !(graph || proposal)) return;
    // A phone opens on the Steps list, where there is no viewport to fit to:
    // the fit waits for the Canvas toggle (below) rather than being spent.
    if (phone && view !== 'canvas') return;
    fitted.current = true;
    window.setTimeout(() => canvasRef.current?.fit(), 0);
  }, [graph, proposal, phone, view]);

  // The same checks the save route runs, with the same two lookups — so
  // nothing the canvas lets you draw can be refused after the fact.
  /**
   * The questions the pickers offer: everything live submissions carry, plus
   * everything the draft is about to add.
   *
   * A person who has just added a field on the Build tab and come here to
   * branch on it should find it — the Logic page offers it, and finding it in
   * one place and not the other is the kind of difference nobody can explain.
   *
   * A UNION rather than the draft alone, because this page is not behind
   * Publish: a branch that already names a field the draft has since deleted
   * still runs against live submissions that still have it, and hiding it here
   * would leave somebody editing a condition they cannot see.
   *
   * The draft-only ones are marked, and `validateGraph` warns about a
   * condition that names one — until the form is published, no submission has
   * that answer, so every one of them takes the other path.
   */
  const fields = useMemo<FieldDefinition[]>(
    () => questionsForWorkflow(publishedFields, draft?.fields),
    [publishedFields, draft?.fields]
  );
  /**
   * Where each question lives, so the value pickers inside a step read like the
   * form rather than like a flat list of labels. Read off the same draft the
   * Logic page reads, which is what makes the two arrangements identical.
   */
  const sections = useMemo(
    () => sectionsFromSpine(draft?.sections, draft?.fields),
    [draft?.sections, draft?.fields]
  );

  /**
   * What a live submission could actually hold — what the warning checks.
   * `parent` is live whenever this form continues anything: it is the order's
   * published answer, not one of ours, so the not-published warning would be
   * about the wrong form.
   */
  const liveFieldKeys = useMemo(
    () => new Set([...publishedFields.map((f) => f.key), ...(parents.length ? ['parent'] : [])]),
    [publishedFields, parents.length]
  );
  /** The form's questions and the order's, for the cards' condition text. */
  const describable = useMemo(() => [...fields, ...parentFieldDefs(parents)], [fields, parents]);

  const problems = useMemo(
    () => (graph
      ? validateGraph(graph, graphContextFor(connections || [], catalog, liveFieldKeys))
      : { errors: [], warnings: [] }),
    [graph, connections, catalog, liveFieldKeys]
  );

  /**
   * Connections this form has that the drawing does not.
   *
   * The Connections tab is gone, so anything not on the canvas is invisible —
   * and it does not run either, because the graph is what runs. Both halves of
   * that need saying out loud. A form adopted from a proposal that somebody
   * then edited, and the connections a template wires up, are the ways to get
   * here now that nothing in the admin writes a connection off-canvas.
   */
  const undrawn = useMemo(
    () => (graph && connections ? undrawnConnections(graph, connections) : []),
    [graph, connections]
  );

  /**
   * The destinations the account can already reach (R6, the rail's green
   * dot): a saved credential for the provider, or a built-in that needs none.
   */
  const connectedTypes = useMemo(
    () => new Set(['email', 'webhook', ...savedCreds.map((c) => c.provider)]),
    [savedCreds]
  );

  /** Put them on the canvas, unattached, so they are visible and wireable. */
  function drawUndrawn() {
    if (!graph || !undrawn.length) return;
    const bottom = graph.nodes.reduce((y, n) => Math.max(y, n.y), 0) + 140;
    commit({
      ...graph,
      nodes: [
        ...graph.nodes,
        ...undrawn.map((c, i) => ({
          id: newNodeId(),
          kind: (c.type === 'invite' ? 'mint' : 'deliver') as WorkflowNode['kind'],
          connectionId: c.id,
          x: 60 + i * 280,
          y: bottom,
        })),
      ],
    });
  }

  /**
   * Every clean change saves itself, like a connection always has.
   *
   * Debounced because dragging a card is a hundred changes, not one; held back
   * entirely while an error stands, because the last saved workflow is what
   * keeps running and overwriting it with something that cannot run would be
   * the one genuinely destructive thing this page could do.
   */
  const pending = useRef<WorkflowGraph | null>(null);
  const timer = useRef<number | null>(null);

  /**
   * One place every failed save lands.
   *
   * A 402 is the plan gate, not a failure: the workflow is fine, the plan does
   * not carry this shape. It gets the house's block, which names the plan that
   * does. Everything else is an error pill.
   *
   * This is one function rather than three because the first cut handled the
   * 402 only in the debounced save — so a refused ADOPTION, which is the most
   * likely way to meet the gate, did nothing at all and said nothing at all.
   */
  const noteSaveFailure = useCallback((e: unknown) => {
    const refusal = (e as { body?: PlanLimitBody }).body;
    if (refusal?.error === 'plan_limit') { setLimitHit(refusal); setSave({ state: 'idle' }); return; }
    setSave({ state: 'error', message: (e as Error).message });
  }, []);
  const commit = useCallback((next: WorkflowGraph, opts: CommitOptions = {}) => {
    setGraph(next);
    if (!adopted) return;                       // a proposal is not saved by touching it
    historyActions.push(next, opts);
    pending.current = next;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const graphNow = pending.current;
      if (!graphNow) return;
      if (validateGraph(graphNow).errors.length) return;
      setSave({ state: 'saving' });
      inFlight.current = saveFormWorkflowApi(form.id, graphNow)
        .then(() => { pending.current = null; setSave({ state: 'idle' }); noteDocumentDraft(); })
        .catch(noteSaveFailure);
    }, 600);
  }, [adopted, form.id, historyActions, noteSaveFailure, noteDocumentDraft]);

  /** The save on its way, if there is one — Publish waits for it. */
  const inFlight = useRef<Promise<unknown> | null>(null);

  /**
   * Leaving the page does not throw the last change away.
   *
   * The debounce that keeps a drag from firing a hundred saves also means the
   * last one is still waiting when somebody clicks another tab. Clearing the
   * timer on unmount — which is the reflex — silently loses whatever they did
   * in the previous half second, on a page whose whole promise is that what is
   * drawn is what runs. So it is flushed instead.
   */
  // Read by the unmount cleanup below, which closes over nothing else and so
  // cannot see the current values any other way.
  const adoptedRef = useRef(adopted);
  const formIdRef = useRef(form.id);
  adoptedRef.current = adopted;
  formIdRef.current = form.id;

  /** Save whatever is still waiting, now, and wait for it — what leaving the page and Publish both need. */
  const flush = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    if (inFlight.current) { try { await inFlight.current; } catch { /* reported where it failed */ } }
    const last = pending.current;
    pending.current = null;
    if (!last || !adoptedRef.current) return;
    if (validateGraph(last).errors.length) return;
    await saveFormWorkflowApi(formIdRef.current, last);
    // The workspace outlives this tab: it still needs to know the form has unpublished changes.
    noteDocumentDraft();
  }, [noteDocumentDraft]);

  useEffect(() => () => { void flush().catch(() => undefined); }, [flush]);
  // The drawing is part of what Publish makes live (085), so Publish waits for it.
  useEffect(() => registerBeforePublish(flush), [registerBeforePublish, flush]);

  /**
   * Step back, and save it.
   *
   * An undo on a live workflow is a change like any other — leaving the canvas
   * showing one thing while the engine ran another would be the worst possible
   * outcome on a page whose entire promise is that the drawing is what runs.
   */
  const step = useCallback((back: boolean) => {
    const next = back ? historyActions.undo() : historyActions.redo();
    if (!next || !adopted) return;
    setGraph(next);
    setSelected(null);
    if (validateGraph(next).errors.length) return;
    setSave({ state: 'saving' });
    inFlight.current = saveFormWorkflowApi(form.id, next)
      .then(() => { setSave({ state: 'idle' }); noteDocumentDraft(); })
      .catch(noteSaveFailure);
  }, [adopted, form.id, historyActions, noteSaveFailure, noteDocumentDraft]);

  /**
   * Every form has a workflow. This is where one comes from.
   *
   * There is no longer an offer to accept or decline: a form that has never
   * had one gets the drawing of what it already does — its connections, in the
   * order they run, and an ending carrying its thank-you — written the first
   * time somebody opens this page. A form with nothing on it gets the shortest
   * true workflow there is: a submission arrives, and it ends.
   *
   * It is written rather than merely shown because the ENDING lives here now.
   * Settings no longer has an After-submitting block, so a workflow somebody
   * has to accept before they can edit their thank-you would be a page asking
   * permission to be itself.
   *
   * The one thing that can refuse is the plan gate: a proposal that branches on
   * a plan without branching workflows stays unwritten, and the canvas shows it
   * read-only beside the block that explains why. The form keeps running its
   * connection list, exactly as it did.
   */
  const materialise = useCallback(async (offer: WorkflowGraph) => {
    setSave({ state: 'saving' });
    setLimitHit(null);
    try {
      // The one live write: the drawing of what this form already does.
      await saveFormWorkflowApi(form.id, offer, { first: true });
      await loadWorkflow();
      setSave({ state: 'idle' });
    } catch (e) { noteSaveFailure(e); }
  }, [form.id, loadWorkflow, noteSaveFailure]);

  // A form with no drawing gets one, once, the first time this page is opened.
  // Not an adoption — a workflow is not optional — just the moment the drawing
  // it has always had stops being computed and starts being editable.
  useEffect(() => {
    if (adopted || !proposal || materialised.current) return;
    materialised.current = true;
    void materialise(proposal);
  }, [adopted, proposal, materialise]);

  // ---- adding a step ------------------------------------------------------
  /**
   * A new step lands wired in, not floating.
   *
   * Dropped below the selection, taking over whatever the selection pointed at
   * — spliced in, with everything below pushed down out of its way — so adding
   * a step in the middle of a flow is one gesture rather than one gesture plus
   * two arrow-redraws. `insertNode` holds each arrow to the same rule a drag
   * is held to, and a step the rules will not attach lands loose.
   */
  async function addEntry(entry: PaletteEntry, at?: { x: number; y: number }) {
    if (!graph || !adopted) return;
    // Where it lands, and what it hangs from. An ENDING has no outputs, so
    // "the selected step's first port" is not a question with an answer —
    // `attachmentFor` falls back to an open tail, or to nothing at all.
    const attach = at ? null : attachmentFor(graph, selected);
    const anchor = attach ? graph.nodes.find((n) => n.id === attach.from) : undefined;
    const x = at?.x ?? (anchor ? anchor.x : 380);
    const y = at?.y ?? (anchor ? anchor.y + assumedHeight(anchor) + 50 : 140);

    let node: WorkflowNode;
    // A follow-up link is structural in the rail because it is part of the
    // flow's shape, but it is still a connection underneath — the invite is
    // where the target form and the link's lifetime live.
    if (entry.key === 'mint') {
      try {
        const r = await createConnectionApi(form.id, { type: 'invite', name: 'Invite to continue', config: {} });
        await stepsChanged();
        node = { id: newNodeId(), kind: 'mint', connectionId: r.connection.id, x, y };
      } catch (e: any) { setError(e.message); return; }
    } else if (entry.structural) {
      node = {
        id: newNodeId(), kind: entry.key as WorkflowNode['kind'], x, y,
        ...(entry.key === 'branch' ? { config: { name: '', when: null, labels: [] } } : {}),
        ...(entry.key === 'chase' ? { config: { days: [3, 7] } } : {}),
      };
    } else {
      try {
        const spec = catalog.find((c) => c.type === entry.key);
        const r = await createConnectionApi(form.id, { type: entry.key, name: spec?.title || entry.key, config: {} });
        await stepsChanged();
        node = {
          id: newNodeId(),
          kind: entry.key === 'invite' ? 'mint' : 'deliver',
          connectionId: r.connection.id, x, y,
        };
      } catch (e: any) { setError(e.message); return; }
    }

    commit(insertNode(graph, node, attach, { height: assumedHeight(node), gap: 50 }));
    setSelected(node.id);
    if (!entry.structural || entry.key === 'mint' || entry.key === 'chase') openNode(node.id);
  }

  /**
   * Turn a no-ending ghost into a real ending.
   *
   * Prefilled from Settings, because that is exactly what this path gets
   * today — so accepting the ghost changes nothing about what happens, it only
   * makes it visible and editable. That is the whole trick of this page.
   */
  function addEnding(fromId: string, port: string, x: number, y: number) {
    if (!graph) return;
    const node: WorkflowNode = {
      id: newNodeId(), kind: 'end', x, y,
      config: { ...(form.settings?.completion || {}) },
    };
    commit({
      nodes: [...graph.nodes, node],
      edges: [...graph.edges, { from: fromId, port, to: node.id }],
    });
    setSelected(node.id);
    openNode(node.id);
  }

  /**
   * Remove a step, and the connection behind it when nothing else needs it.
   *
   * Deleting the connection too is the important half. The Connections tab is
   * gone, so a connection with no step on the canvas is a connection nobody
   * can see, edit or delete — an invisible thing that still exists, which is
   * the exact class of problem this page was built to end. Its delivery
   * history survives regardless: `deliveries.connection_id` is ON DELETE SET
   * NULL and the row keeps its `connection_type`.
   *
   * Unless another step points at the same connection. Two steps sharing one
   * connection is a shape the design explicitly blesses — two paths that want
   * the same email — and the second one still needs it.
   *
   * R6: the gap closes. A step with one parent and one child hands its parent's
   * arrow on to its child where the edge rules allow (`relinkAround`), so
   * deleting a step in the middle of a flow does not orphan everything below.
   */
  async function removeNode(id: string) {
    if (!graph) return;
    const node = graph.nodes.find((n) => n.id === id);
    if (!node) return;
    // The floor every workflow has. Refused here as well as hidden from the
    // menu, because Delete is also a key.
    const refuse = undeletable(graph, id);
    if (refuse) { setError(refuse); return; }

    const conn = node.connectionId ? connections?.find((c) => c.id === node.connectionId) : undefined;
    const alsoDeleteConnection = !!conn && connectionIsOrphaned(graph, id);
    const what = conn
      ? (alsoDeleteConnection
        ? `Delete “${conn.name}”?

The step and its settings go for good. Deliveries already sent keep their history.`
        : `Remove this step?

Another step still points at “${conn.name}”, so its settings are kept.`)
      : 'Remove this step from the workflow?';
    if (!window.confirm(what)) return;

    const relink = relinkAround(graph, id);
    commit({
      nodes: graph.nodes.filter((n) => n.id !== id),
      edges: [...graph.edges.filter((e) => e.from !== id && e.to !== id), ...(relink ? [relink] : [])],
    // No undo brings a connections row back, so rather than offer one that
    // would restore a node pointing at nothing, this is where history stops.
    }, { barrier: alsoDeleteConnection });
    setSelected(null);
    if (editing === id) setEditing(null);
    if (alsoDeleteConnection && conn) {
      try { await deleteConnectionApi(conn.id); await stepsChanged(); }
      catch (e: any) { setError(e.message); }
    }
  }

  /** Rename what the card says: the connection's name, or the branch's. */
  async function renameNode(id: string) {
    if (!graph) return;
    const node = graph.nodes.find((n) => n.id === id);
    if (!node) return;
    const conn = node.connectionId ? connections?.find((c) => c.id === node.connectionId) : undefined;
    const current = conn ? conn.name : String(node.config?.name || '');
    const next = window.prompt('What should this step be called?', current);
    if (next === null) return;
    const name = next.trim();
    if (!name || name === current) return;
    if (conn) {
      try { await updateConnectionApi(conn.id, { name }); await stepsChanged(); }
      catch (e: any) { setError(e.message); }
    } else {
      patchNode(id, { config: { ...(node.config || {}), name } });
    }
  }

  /**
   * A second step like this one, with settings of its own.
   *
   * This used to draw a second step pointing at the SAME connection, on the
   * reasoning that two paths wanting the same email should be written once.
   * Nothing on screen said the two were one, so the first person to give two
   * branches different CC lists changed both (Ben, 2026-10-07). The connection
   * is copied now — by the server, which is the only place its secrets are —
   * and the new step points at the copy. Two steps that already share keep
   * sharing until `splitNode` is asked to part them.
   */
  async function duplicateNode(id: string) {
    if (!graph) return;
    const node = graph.nodes.find((n) => n.id === id);
    if (!node || node.kind === 'start') return;
    let connectionId = node.connectionId;
    if (connectionId) {
      try {
        connectionId = (await copyConnectionApi(connectionId)).connection.id;
        await stepsChanged();
      } catch (e: any) { setError(e.message); return; }
    }
    const copy: WorkflowNode = {
      ...node,
      id: newNodeId(),
      ...(connectionId ? { connectionId } : {}),
      x: node.x + 280,
      y: node.y,
      ...(node.config ? { config: { ...node.config } } : {}),
    };
    commit({ ...graph, nodes: [...graph.nodes, copy] });
    setSelected(copy.id);
  }

  /**
   * Part a step from the others that share its connection.
   *
   * For the workflows drawn before `duplicateNode` copied: this step gets a
   * copy of the settings and points at it, and the others keep the original.
   * History stops here for the same reason it does on a delete — an undo would
   * point the step back at the shared settings and leave the copy invisible.
   */
  async function splitNode(id: string) {
    if (!graph) return;
    const node = graph.nodes.find((n) => n.id === id);
    if (!node?.connectionId || connectionIsOrphaned(graph, id)) return;
    try {
      const { connection } = await copyConnectionApi(node.connectionId);
      await stepsChanged();
      commit({
        ...graph,
        nodes: graph.nodes.map((n) => (n.id === id ? { ...n, connectionId: connection.id } : n)),
      }, { barrier: true });
    } catch (e: any) { setError(e.message); }
  }

  /** Switch a step off, or put it in test mode — the connection's own flags. */
  async function toggleConnection(id: string, patch: { enabled?: boolean; dry_run?: boolean }) {
    const node = graph?.nodes.find((n) => n.id === id);
    if (!node?.connectionId) return;
    try { await updateConnectionApi(node.connectionId, patch); await stepsChanged(); }
    catch (e: any) { setError(e.message); }
  }

  // Delete removes the selected step. Kept off inputs so typing in the editor
  // never deletes the thing being edited, and off altogether while a step's
  // dialog is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target?.isContentEditable;
      if (typing || editing) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        step(!e.shiftKey);
        return;
      }
      if (meta && e.key.toLowerCase() === 'y') { e.preventDefault(); step(false); return; }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (selected) { e.preventDefault(); void removeNode(selected); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const patchNode = (id: string, patch: Partial<WorkflowNode>, opts?: CommitOptions) => {
    if (!graph) return;
    // A branch path removed takes its arrow with it, in the same change — so
    // one undo brings both back.
    commit(withoutDeadPorts({ ...graph, nodes: graph.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }), opts);
  };

  /** What to call a step in the problems list — its own name, not its id. */
  function nameOfNode(id: string): string {
    const node = (graph || proposal)?.nodes.find((n) => n.id === id);
    if (!node) return 'a step';
    if (node.kind === 'branch') return String(node.config?.name || 'a branch');
    if (node.kind === 'end') return 'an ending';
    if (node.kind === 'merge') return 'a rejoin';
    const conn = node.connectionId ? connections?.find((c) => c.id === node.connectionId) : undefined;
    return conn?.name || node.kind;
  }

  /**
   * Select the step a problem is about, and bring it into view.
   *
   * A problems list that names a step you then have to go and find is a list
   * that gets read once.
   */
  function showMe(id: string) {
    setProblemsOpen(false);
    setSelected(id);
    canvasRef.current?.focusCard(id);
  }

  /** The surface's size, for the zoom buttons to zoom about its middle. */
  const surfaceSize = () => {
    const el = document.querySelector('.wf-viewport') as HTMLElement | null;
    return { w: el?.clientWidth || 800, h: el?.clientHeight || 600 };
  };
  const zoomBy = (dir: 1 | -1) => {
    const { w, h } = surfaceSize();
    setCanvasView((v) => zoomStep(v, dir, w, h));
  };
  const fit = () => canvasRef.current?.fit();

  if (!connections || (graph === null && !proposal)) {
    return <div className="fs-card pad">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;
  }

  const showing = graph || proposal || { nodes: [], edges: [] };
  const isProposal = !adopted;
  const editingNode = editing ? showing.nodes.find((n) => n.id === editing) : undefined;

  const pill = save.state === 'saving'
    ? { cls: 'neutral', text: 'Saving…' }
    : save.state === 'error'
      ? { cls: 'danger', text: save.message || 'Not saved' }
      : problems.errors.length
        ? { cls: 'danger', text: `Not saved — fix ${problems.errors.length} ${problems.errors.length === 1 ? 'problem' : 'problems'}` }
        // Saved into the draft with Fields, Logic and the Document (085): what
        // is live keeps running until Publish.
        : { cls: 'info', text: 'In the draft — publish to go live' };

  /**
   * The workflow's own controls (R6 follow-up): the save-state pill, the
   * problems list, undo/redo and the rail toggle. On a desktop and a tablet
   * they sit in the canvas's top-right corner — the design has no toolbar
   * row, the drawing fills the screen — and on a phone in the one-row bar
   * the M6 layout folds them into.
   */
  const controls = (
    <>
      {awaiting !== null && awaiting > 0 && (
        <Link
          className="wf-awaiting wf-desk-only"
          to={`../submissions?${submissionQueryString({
            conditions: [{ column: 'meta:status', op: 'is', value: 'awaiting' }],
          })}`}
        >{awaiting} waiting on someone</Link>
      )}
        {adopted && (
          <span className={`fs-pill ${pill.cls}`} title="The workflow is part of the draft: it goes live when you publish">
            {pill.text}
          </span>
        )}
        {adopted && (problems.errors.length + problems.warnings.length > 0) && (
          <span className="fs-menu-anchor">
            <button
              className={`fs-pill ${problems.errors.length ? 'danger' : 'warn'} wf-problems`}
              onClick={() => setProblemsOpen((v) => !v)}
            >
              {problems.errors.length + problems.warnings.length}
              {problems.errors.length + problems.warnings.length === 1 ? ' problem' : ' problems'} ▾
            </button>
            {/* `onMouseLeave` is the whole dismissal today, and a finger never
                leaves. Under 700 the list is a bottom sheet with a scrim, and
                the scrim is what closes it. */}
            {problemsOpen && <div className="wf-problems-scrim" onClick={() => setProblemsOpen(false)} />}
            {problemsOpen && (
              <div className="wf-problems-list" onMouseLeave={() => setProblemsOpen(false)}>
                {[...problems.errors.map((p) => ({ p, bad: true })),
                  ...problems.warnings.map((p) => ({ p, bad: false }))].map(({ p, bad }, i) => (
                  <div key={i} className={`row${bad ? ' bad' : ''}`}>
                    <span className="mark">!</span>
                    <span className="text">
                      <b>{bad ? 'Error' : 'Warning'}{p.nodeId ? ` · ${nameOfNode(p.nodeId)}` : ''}</b>{' '}
                      {p.message}
                    </span>
                    {p.nodeId && (
                      <button className="show" onClick={() => showMe(p.nodeId!)}>Show me</button>
                    )}
                  </div>
                ))}
                <div className="note">
                  An error means the workflow would <b>do something wrong</b>; it blocks saving, and the
                  last saved one keeps running. A warning means a piece would <b>do nothing</b>; it saves fine.
                </div>
              </div>
            )}
          </span>
        )}
        {adopted && (
          <>
            <button
              className="fs-btn neutral sm wf-desk-only" onClick={() => step(true)} disabled={!history.canUndo}
              title="Undo (Ctrl+Z)"
            >↶</button>
            <button
              className="fs-btn neutral sm wf-desk-only" onClick={() => step(false)} disabled={!history.canRedo}
              title="Redo (Ctrl+Shift+Z)"
            >↷</button>
            <button
              className="fs-btn neutral sm icon wf-rail-toggle" onClick={() => setRailOpen((v) => !v)}
              title={railOpen ? 'Hide the steps rail' : 'Show the steps rail'}
              aria-label={railOpen ? 'Hide the steps rail' : 'Show the steps rail'}
              aria-pressed={railOpen}
            >⊞</button>
          </>
        )}
    </>
  );

  /** The design's overlay over the drawing: the trigger, the zoom, Fit and the dry run on the left; the controls on the right. */
  const overlay = (
    <>
      <div className="wf-overlay-group">
        <span className="wf-trigger-pill">After someone submits</span>
        <span className="wf-zoom">
          <button type="button" onClick={() => zoomBy(-1)} title="Zoom out" aria-label="Zoom out">−</button>
          <span className="level" aria-live="polite">{Math.round(canvasView.zoom * 100)}%</span>
          <button type="button" onClick={() => zoomBy(1)} title="Zoom in" aria-label="Zoom in">+</button>
        </span>
        <button type="button" className="fs-btn neutral sm" onClick={fit} title="Fit the whole drawing">Fit</button>
        {adopted && (
          <button
            type="button" className="fs-btn neutral sm" onClick={() => setTrying(true)}
            title="See what this would do, without sending anything"
          >▷ Try with a sample</button>
        )}
      </div>
      <div className="wf-overlay-group right">{controls}</div>
    </>
  );

  return (
    <div className="wf-page" ref={pageRef}>
      {/* The phone's one-row bar (M6): the controls, and a ⋯ for what it cannot hold. */}
      {phone && (
        <div className="wf-toolbar">
          <span className="spacer" />
          {controls}
          {adopted && (
            <span className="wf-toolbar-more">
              <RowMenu open={moreOpen} onToggle={setMoreOpen} label="Workflow">
                <button disabled={!history.canUndo} onClick={() => step(true)}>Undo</button>
                <button disabled={!history.canRedo} onClick={() => step(false)}>Redo</button>
                <button onClick={fit}>Fit the whole drawing</button>
                <div className="rule" />
                <button onClick={() => setTrying(true)}>Try it</button>
              </RowMenu>
            </span>
          )}
        </div>
      )}

      {/* Steps or Canvas (§4H H2): the list is what a phone opens on, because
          a 240px card on a 900px surface is not a thing a 375px screen shows. */}
      {phone && adopted && (
        <div className="fs-seg wf-view-seg" role="group" aria-label="How to show the workflow">
          <button type="button" aria-pressed={view === 'steps'} className={view === 'steps' ? 'on' : ''}
            onClick={() => setView('steps')}>Steps</button>
          <button type="button" aria-pressed={view === 'canvas'} className={view === 'canvas' ? 'on' : ''}
            onClick={() => setView('canvas')}>Canvas</button>
        </div>
      )}

      {error && <div className="ad-err">{error}</div>}
      {limitHit && (
        <div style={{ margin: '10px 0' }}>
          <PlanLimitBlock body={limitHit} />
        </div>
      )}

      {/*
        Where two steps nobody drew came from.

        Not dismissible, and with no button: it is a fact about this form, not
        an announcement, and a form that carries migrated steps carries them
        whether or not somebody clicked something once. It retires itself on
        the first save of the drawing — see the workflow PUT route.
      */}
      {form.settings.migratedEmails && (
        <div className="wf-adopt">
          <span className="text">
            <b>The two emails from Settings are steps now.</b> “Email me when someone submits” and
            “Send the respondent a confirmation” were switches on a page nobody could see from here.
            They are on the canvas with the addresses and subjects they already had, and they run
            where they are drawn.
          </span>
        </div>
      )}

      {adopted && undrawn.length > 0 && (
        <div className="wf-adopt">
          <span className="text">
            <b>{undrawn.length} {undrawn.length === 1 ? 'connection is' : 'connections are'} not in this
            workflow</b> — {undrawn.map((c) => c.name).join(', ')}. The workflow is what runs, so
            {undrawn.length === 1 ? ' it does' : ' they do'} nothing where
            {undrawn.length === 1 ? ' it is' : ' they are'}. Put
            {undrawn.length === 1 ? ' it' : ' them'} on the canvas to wire
            {undrawn.length === 1 ? ' it' : ' them'} in, or delete
            {undrawn.length === 1 ? ' it' : ' them'} from the step menu once
            {undrawn.length === 1 ? ' it is' : ' they are'} there.
          </span>
          <button className="fs-btn primary sm" onClick={drawUndrawn}>
            Put {undrawn.length === 1 ? 'it' : 'them'} on the canvas
          </button>
        </div>
      )}
      <div className={`wf-frame wf-frame-fixed${phone && view === 'canvas' ? ' wf-readonly' : ''}`}>
        {adopted && railOpen && !narrow && (
          <PaletteRail
            catalog={catalog} search={search} onSearch={setSearch}
            onAdd={(e) => addEntry(e)} onDragEntry={setDragging}
            connectedTypes={connectedTypes}
          />
        )}
        <WorkflowCanvas
          ref={canvasRef}
          mode={phone && view === 'steps' ? 'steps' : 'canvas'}
          graph={showing}
          connections={connections}
          catalog={catalog}
          fields={describable}
          proposed={isProposal}
          provenance={isProposal ? provenance : undefined}
          problems={problems.errors.concat(problems.warnings)}
          errorIds={new Set(problems.errors)}
          selected={selected}
          onSelect={setSelected}
          onOpen={openNode}
          onChange={adopted && !(phone && view === 'canvas') ? commit : undefined}
          onDropEntry={adopted && dragging
            ? (x, y) => { const e = dragging; setDragging(null); if (e) void addEntry(e, { x, y }); }
            : undefined}
          onDelete={adopted ? (id) => { void removeNode(id); } : undefined}
          undeletable={(id) => (graph ? undeletable(graph, id) : null)}
          onRename={adopted ? (id) => { void renameNode(id); } : undefined}
          onDuplicate={adopted ? (id) => { void duplicateNode(id); } : undefined}
          onSplit={adopted ? (id) => { void splitNode(id); } : undefined}
          onConnectFrom={adopted ? setConnecting : undefined}
          onConnectBefore={adopted && narrow ? setConnectingBefore : undefined}
          onToggleConnection={adopted ? (id, patch) => { void toggleConnection(id, patch); } : undefined}
          onAddEnding={adopted ? addEnding : undefined}
          view={canvasView}
          onViewChange={setCanvasView}
          overlay={!phone ? overlay : undefined}
          formName=""
        />
      </div>

      {/* §4H H1/H3: one "+" instead of a column, and the same catalog as a
          sheet. Both exist only under 900. */}
      {narrow && adopted && (
        <button
          className="wf-add-fab" title="Add a step" aria-label="Add a step"
          onClick={() => { setSearch(''); setRailSheet(true); }}
        >+</button>
      )}
      {narrow && railSheet && (
        <Modal title="Add a step" onClose={() => setRailSheet(false)}>
          <div className="wf-sheet-list">
            <PaletteRail
              catalog={catalog} search={search} onSearch={setSearch}
              onAdd={(e) => { setRailSheet(false); void addEntry(e); }}
              onDragEntry={setDragging}
              connectedTypes={connectedTypes}
              sheet
            />
          </div>
        </Modal>
      )}

      {connecting && graph && graph.nodes.some((n) => n.id === connecting) && (
        <ConnectModal
          graph={graph}
          node={graph.nodes.find((n) => n.id === connecting)!}
          connections={connections}
          catalog={catalog}
          onConnect={(from, port, to) => commit({ ...graph, edges: [...graph.edges, { from, port, to }] })}
          onClose={() => setConnecting(null)}
        />
      )}

      {connectingBefore && graph && graph.nodes.some((n) => n.id === connectingBefore) && (
        <ConnectModal
          direction="before"
          graph={graph}
          node={graph.nodes.find((n) => n.id === connectingBefore)!}
          connections={connections}
          catalog={catalog}
          onConnect={(from, port, to) => commit({ ...graph, edges: [...graph.edges, { from, port, to }] })}
          onClose={() => setConnectingBefore(null)}
        />
      )}

      {trying && graph && (
        <TryItModal
          formId={form.id} graph={graph} connections={connections} catalog={catalog}
          onClose={() => setTrying(false)}
        />
      )}

      {editingNode && (
        <NodeEditorModal
          // Keyed, so moving to another step by its chip starts from that
          // step's own values rather than the ones being typed into this one.
          key={editingNode.id}
          graph={showing}
          node={editingNode}
          connections={connections}
          catalog={catalog}
          fields={fields}
          widgetScopes={widgetScopes}
          sections={sections}
          parents={parents}
          otherForms={otherForms}
          credProviders={credProviders}
          savedCreds={savedCreds}
          canManageCredentials={canManageCredentials}
          onClose={() => setEditing(null)}
          onSaved={() => { void stepsChanged(); }}
          onPatchNode={patchNode}
          onGoTo={openNode}
          onToggleConnection={adopted ? (id, patch) => { void toggleConnection(id, patch); } : undefined}
        />
      )}
    </div>
  );
}

export { deleteConnectionApi, updateConnectionApi };
